import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { SABER_BLUE, SABER_GREEN, STAR_GRAY, TEXT_LIGHT } from '../constants';
import { HoloCard } from '../ui-helpers';

const KEYFRAMES_ID = 'shield-gen-keyframes';

function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes shield-spin-cw { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }
    @keyframes shield-spin-ccw { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(-360deg); } }
    @keyframes shield-pulse-glow { 0%, 100% { box-shadow: 0 0 20px #93FF1840, 0 0 40px #93FF1820; } 50% { box-shadow: 0 0 35px #93FF1880, 0 0 70px #93FF1840, 0 0 100px #93FF1820; } }
    @keyframes shield-energy-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
    @keyframes shield-particle-orbit { from { transform: rotate(var(--start-angle)) translateX(var(--orbit-radius)) rotate(calc(-1 * var(--start-angle))); } to { transform: rotate(calc(var(--start-angle) + 360deg)) translateX(var(--orbit-radius)) rotate(calc(-1 * (var(--start-angle) + 360deg))); } }
  `;
  document.head.appendChild(style);
}

function useAnimatedCounter(end: number, duration: number, active: boolean, suffix = '') {
  const [display, setDisplay] = useState(`0${suffix}`);

  useEffect(() => {
    if (!active) return undefined;
    const startTime = performance.now();
    let raf: number;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      const current = Math.round(eased * end);
      setDisplay(`${current}${suffix}`);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, end, duration, suffix]);

  return display;
}

function useTypingText(text: string, active: boolean, speed = 50) {
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    if (!active) {
      setDisplayed('');
      return undefined;
    }
    let idx = 0;
    setDisplayed('');
    const interval = setInterval(() => {
      idx += 1;
      setDisplayed(text.slice(0, idx));
      if (idx >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [active, text, speed]);

  return displayed;
}

const PARTICLES = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  angle: (360 / 8) * i,
  radius: 100 + (i % 3) * 15,
  duration: 4 + (i % 3) * 2,
  size: 3 + (i % 2) * 2,
  color: i % 2 === 0 ? '#93FF18' : '#2ECDFF',
}));

const RINGS = [
  { size: 200, color: '#2ECDFF', opacity: 0.15, borderWidth: 1, direction: 'cw' as const, speed: 20, dashed: true },
  { size: 160, color: '#93FF18', opacity: 0.25, borderWidth: 1.5, direction: 'ccw' as const, speed: 15, dashed: true },
  { size: 120, color: '#2ECDFF', opacity: 0.4, borderWidth: 2, direction: 'cw' as const, speed: 25, dashed: false },
  { size: 90, color: '#93FF18', opacity: 0.55, borderWidth: 2, direction: 'ccw' as const, speed: 12, dashed: false },
];

const STATS: { endValue: number; suffix: string; prefix: string; labelKey: string }[] = [
  { endValue: 2, suffix: '+', prefix: '', labelKey: 'shield.stat_isps' },
  { endValue: 10, suffix: '', prefix: '', labelKey: 'shield.stat_routes' },
  { endValue: 0, suffix: '%', prefix: '', labelKey: 'shield.stat_downtime' },
];

export function ShieldGenerator() {
  const { t } = useTranslation(['components/trial/lp/v4b']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  useEffect(() => {
    ensureKeyframes();
  }, []);

  const counter0 = useAnimatedCounter(STATS[0]?.endValue ?? 0, 1200, inView, STATS[0]?.suffix);
  const counter1 = useAnimatedCounter(STATS[1]?.endValue ?? 0, 1500, inView);
  const counter2 = useAnimatedCounter(STATS[2]?.endValue ?? 0, 800, inView, STATS[2]?.suffix);
  const counters = [counter0, counter1, counter2];

  const statusText = useTypingText('SHIELDS_ACTIVE... ALL_SYSTEMS_NOMINAL', inView, 55);

  return (
    <div ref={ref}>
      <HoloCard className='relative overflow-hidden p-6 md:p-8' glow={SABER_BLUE}>
        <div className='pointer-events-none absolute inset-0' style={{ background: `linear-gradient(135deg, ${SABER_BLUE}06 0%, ${SABER_GREEN}04 25%, transparent 50%, ${SABER_BLUE}06 75%, ${SABER_GREEN}04 100%)`, backgroundSize: '400% 400%', animation: inView ? 'shield-energy-bg 8s ease infinite' : 'none', zIndex: 0 }} />

        <div className='relative z-10 grid items-center gap-8 md:grid-cols-2'>
          <div className='relative flex items-center justify-center' style={{ minHeight: 280 }}>
            {RINGS.map((ring, i) => (
              <div key={`ring-${i}`} className='absolute rounded-full' style={{ width: ring.size, height: ring.size, left: '50%', top: '50%', border: `${ring.borderWidth}px ${ring.dashed ? 'dashed' : 'solid'} ${ring.color}`, opacity: inView ? ring.opacity : 0, animation: inView ? `shield-spin-${ring.direction} ${ring.speed}s linear infinite` : 'none', transition: 'opacity 0.6s ease', transitionDelay: `${i * 0.15}s`, boxShadow: `0 0 ${8 + i * 4}px ${ring.color}20` }} />
            ))}

            {PARTICLES.map((p) => (
              <div key={`particle-${p.id}`} className='absolute' style={{ width: p.size, height: p.size, left: '50%', top: '50%', marginLeft: -(p.size / 2), marginTop: -(p.size / 2), ['--start-angle' as string]: `${p.angle}deg`, ['--orbit-radius' as string]: `${p.radius}px`, animation: inView ? `shield-particle-orbit ${p.duration}s linear infinite` : 'none', opacity: inView ? 0.8 : 0, transition: 'opacity 0.5s ease', transitionDelay: `${0.3 + p.id * 0.08}s` }}>
                <div style={{ width: p.size, height: p.size, borderRadius: '50%', background: p.color, boxShadow: `0 0 ${p.size * 2}px ${p.color}90` }} />
              </div>
            ))}

            <motion.div className='relative z-10 flex size-20 items-center justify-center rounded-full' style={{ background: `radial-gradient(circle, ${SABER_GREEN}35 0%, ${SABER_GREEN}10 50%, transparent 70%)`, border: `2px solid ${SABER_GREEN}`, animation: inView ? 'shield-pulse-glow 2.5s ease-in-out infinite' : 'none' }} initial={{ opacity: 0, scale: 0 }} animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }} transition={{ delay: 0.4, type: 'spring', stiffness: 200, damping: 15 }}>
              <span className='text-3xl' style={{ filter: 'drop-shadow(0 0 8px #93FF1880)' }}>
                {'\uD83D\uDEE1\uFE0F'}
              </span>
            </motion.div>
          </div>

          <div>
            <h3 className='mb-2 text-xl font-bold md:text-2xl' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-bai)' }}>
              {t('shield.title')}
            </h3>

            <div className='mb-4 font-mono text-xs tracking-widest' style={{ color: SABER_GREEN, minHeight: '1.2em' }}>
              <span>{statusText}</span>
              <span style={{ opacity: statusText.length < 38 ? 1 : 0, borderInlineEnd: '2px solid currentColor', marginInlineStart: 2, paddingInlineEnd: 1 }} className='animate-pulse' />
            </div>

            <p className='mb-5 text-sm leading-relaxed md:text-base' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
              {t('shield.description')}
            </p>

            <div className='grid grid-cols-3 gap-3'>
              {STATS.map((stat, i) => (
                <motion.div key={stat.labelKey} className='relative overflow-hidden rounded-lg border py-3 text-center' style={{ borderColor: `${SABER_BLUE}30`, backgroundColor: `${SABER_BLUE}08` }} initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }} transition={{ delay: 0.6 + i * 0.15, type: 'spring', stiffness: 120 }} whileHover={{ borderColor: `${SABER_GREEN}60`, backgroundColor: `${SABER_GREEN}10`, scale: 1.05, transition: { duration: 0.2 } }}>
                  <div className='pointer-events-none absolute inset-0' style={{ background: `linear-gradient(135deg, transparent 40%, ${SABER_GREEN}08 50%, transparent 60%)`, backgroundSize: '200% 200%', animation: inView ? 'shield-energy-bg 4s ease infinite' : 'none' }} />
                  <div className='relative text-2xl font-bold tabular-nums' style={{ color: SABER_GREEN, fontFamily: 'var(--font-bai)' }}>
                    {stat.prefix}
                    {counters[i]}
                  </div>
                  <div className='relative mt-1 text-xs' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(stat.labelKey)}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </HoloCard>
    </div>
  );
}
